import { useState } from 'react';
import { Plus, Trash2, CreditCard as Edit2, TrendingDown, X, ToggleLeft, ToggleRight, Calendar } from 'lucide-react';
import { usePMEFixedExpenses, PMEFixedExpenseInsert } from '../../hooks/usePMEFixedExpenses';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getDaysUntil(dateStr: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: 'Mensuelle',
  quarterly: 'Trimestrielle',
  yearly: 'Annuelle',
};

interface ExpenseFormData {
  label: string;
  amount: string;
  frequency: 'monthly' | 'quarterly' | 'yearly';
  next_payment_date: string;
  is_active: boolean;
  notes: string;
}

const DEFAULT_FORM: ExpenseFormData = {
  label: '',
  amount: '',
  frequency: 'monthly',
  next_payment_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  is_active: true,
  notes: '',
};

interface ExpenseModalProps {
  initial?: ExpenseFormData & { id?: string };
  onClose: () => void;
  onSave: (data: PMEFixedExpenseInsert) => Promise<void>;
  title: string;
}

function ExpenseModal({ initial, onClose, onSave, title }: ExpenseModalProps) {
  const [form, setForm] = useState<ExpenseFormData>(initial || DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field: keyof ExpenseFormData, value: string | boolean) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.label.trim()) { setError('Le libellé est requis'); return; }
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0) {
      setError('Le montant doit être supérieur à 0'); return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        label: form.label.trim(),
        amount: Number(form.amount),
        frequency: form.frequency,
        next_payment_date: form.next_payment_date,
        is_active: form.is_active,
        notes: form.notes,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Ex: Loyer, Assurance, Salaire..."
              value={form.label}
              onChange={e => set('label', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Montant (€) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="0"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fréquence</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                value={form.frequency}
                onChange={e => set('frequency', e.target.value)}
              >
                <option value="monthly">Mensuelle</option>
                <option value="quarterly">Trimestrielle</option>
                <option value="yearly">Annuelle</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prochaine échéance</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={form.next_payment_date}
              onChange={e => set('next_payment_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optionnel)</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
              rows={2}
              placeholder="Informations complémentaires..."
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => set('is_active', !form.is_active)}
              className={`w-10 h-6 rounded-full transition-colors relative ${form.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
            <span className="text-sm font-medium text-gray-700">Charge active</span>
          </label>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} fullWidth>Annuler</Button>
            <Button type="submit" loading={saving} fullWidth>Enregistrer</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function PMEExpensesPage() {
  const { expenses, totalMonthlyBurden, loading, addExpense, updateExpense, deleteExpense, getMonthlyEquivalent } = usePMEFixedExpenses();
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<(ExpenseFormData & { id: string }) | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const filtered = expenses.filter(e =>
    filter === 'all' ? true : filter === 'active' ? e.is_active : !e.is_active
  );

  const totalYearly = totalMonthlyBurden * 12;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {showModal && (
        <ExpenseModal
          title="Nouvelle charge fixe"
          onClose={() => setShowModal(false)}
          onSave={addExpense}
        />
      )}
      {editItem && (
        <ExpenseModal
          title="Modifier la charge"
          initial={editItem}
          onClose={() => setEditItem(null)}
          onSave={data => updateExpense(editItem.id, data)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Charges fixes</h2>
          <p className="text-sm text-gray-500 mt-0.5">Gérez vos charges récurrentes et leur impact mensuel</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus size={16} />
          Ajouter
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
              <TrendingDown size={18} className="text-red-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Charge mensuelle</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalMonthlyBurden)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <Calendar size={18} className="text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Charge annuelle</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totalYearly)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <TrendingDown size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Nombre de charges</p>
              <p className="text-lg font-bold text-gray-900">
                {expenses.filter(e => e.is_active).length} <span className="text-sm font-normal text-gray-400">actives</span>
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card padding="none">
        <div className="flex items-center gap-1 p-4 border-b border-gray-100">
          {(['all', 'active', 'inactive'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {f === 'all' ? 'Toutes' : f === 'active' ? 'Actives' : 'Inactives'}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} charge{filtered.length > 1 ? 's' : ''}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <TrendingDown size={40} className="text-gray-200 mb-3" />
            <p className="text-sm font-medium text-gray-500">Aucune charge</p>
            <p className="text-xs text-gray-400 mt-1">Cliquez sur "Ajouter" pour enregistrer votre première charge</p>
          </div>
        ) : (
          <>
            <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-50">
              <div className="col-span-3">Libellé</div>
              <div className="col-span-2 text-right">Montant</div>
              <div className="col-span-2 text-right">Equiv. mensuel</div>
              <div className="col-span-2">Fréquence</div>
              <div className="col-span-2">Prochaine échéance</div>
              <div className="col-span-1"></div>
            </div>

            <div className="divide-y divide-gray-50">
              {filtered.map(expense => {
                const days = getDaysUntil(expense.next_payment_date);
                const isOverdue = days < 0;
                const isUrgent = days >= 0 && days <= 7;
                const monthly = getMonthlyEquivalent(expense);

                return (
                  <div
                    key={expense.id}
                    className={`grid grid-cols-12 gap-4 px-4 py-3.5 items-center hover:bg-gray-50 transition-colors ${
                      !expense.is_active ? 'opacity-50' : ''
                    } ${isOverdue && expense.is_active ? 'bg-red-50/40' : ''}`}
                  >
                    <div className="col-span-12 sm:col-span-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <TrendingDown size={14} className="text-red-500" />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${expense.is_active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                            {expense.label}
                          </p>
                          <p className="text-xs text-gray-400 sm:hidden">
                            {formatCurrency(expense.amount)} · {FREQUENCY_LABELS[expense.frequency]}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="hidden sm:block col-span-2 text-right">
                      <span className="text-sm font-semibold text-gray-900">{formatCurrency(expense.amount)}</span>
                    </div>

                    <div className="hidden sm:block col-span-2 text-right">
                      <span className="text-sm text-gray-500">
                        {expense.frequency !== 'monthly' ? formatCurrency(monthly) : '—'}
                      </span>
                    </div>

                    <div className="hidden sm:block col-span-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        expense.frequency === 'monthly' ? 'bg-blue-50 text-blue-700' :
                        expense.frequency === 'quarterly' ? 'bg-orange-50 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {FREQUENCY_LABELS[expense.frequency]}
                      </span>
                    </div>

                    <div className="hidden sm:block col-span-2">
                      <p className="text-sm text-gray-700">{formatDate(expense.next_payment_date)}</p>
                      {isOverdue && expense.is_active && (
                        <p className="text-xs text-red-600 font-medium">En retard de {Math.abs(days)}j</p>
                      )}
                      {isUrgent && expense.is_active && (
                        <p className="text-xs text-orange-600 font-medium">Dans {days}j</p>
                      )}
                    </div>

                    <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-1">
                      <button
                        onClick={() => updateExpense(expense.id, { is_active: !expense.is_active })}
                        className={`p-1.5 rounded-lg transition-colors ${expense.is_active ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        title={expense.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {expense.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => setEditItem({
                          id: expense.id,
                          label: expense.label,
                          amount: String(expense.amount),
                          frequency: expense.frequency,
                          next_payment_date: expense.next_payment_date,
                          is_active: expense.is_active,
                          notes: expense.notes || '',
                        })}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(expense.id)}
                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Supprimer la charge ?</h3>
            <p className="text-sm text-gray-600 mb-6">Cette action est irréversible.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} fullWidth>Annuler</Button>
              <Button variant="danger" onClick={async () => { await deleteExpense(confirmDelete); setConfirmDelete(null); }} fullWidth>Supprimer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
